import React, { useState, useRef, useEffect } from "react";
import { classNames } from "primereact/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import "./Person.css";
import axios from "axios";
import { Editor } from "primereact/editor";
import { FileUpload } from "primereact/fileupload";
import Resizer from "react-image-file-resizer";

function Person() {
  let emptyperson = {
    id: null,
    name: "",
    position: "",
    description: "",
    sortOrder: null,
    imageUrl: null,
    signature: null,
  };

  const [persons, setpersons] = useState(null);
  const [personDialog, setpersonDialog] = useState(false);
  const [deletepersonDialog, setDeletepersonDialog] = useState(false);
  const [deletepersonsDialog, setDeletepersonsDialog] = useState(false);
  const [person, setperson] = useState(emptyperson);
  const [selectedpersons, setSelectedpersons] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [file, setFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const dt = useRef(null);

  const isFetched = useRef(false); // ✅ Prevent duplicate API calls in Strict Mode

  useEffect(() => {
    if (!isFetched.current) {
      getpersons();
      isFetched.current = true; // ✅ Ensure it runs only once
    }
  }, []);

  const getpersons = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_URL + "api/People"
      );

      if (response.data) {
        setpersons(response.data);
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error Fetching persons",
        life: 3000,
      });
    }
  };

  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        800, // width
        800, // height
        "JPEG", // format (can be "JPEG", "PNG", "WEBP")
        100, // quality (0-100)
        0, // rotation (optional)
        (uri) => {
          resolve(uri);
        },
        "blob" // output type (blob or base64)
      );
    });

  const openNew = () => {
    setperson(emptyperson);
    setSubmitted(false);
    setpersonDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setpersonDialog(false);
  };

  const hideDeleteProductDialog = () => {
    setDeletepersonDialog(false);
  };

  const hideDeleteProductsDialog = () => {
    setDeletepersonsDialog(false);
  };

  const saveperson = async () => {
    setLoading(true);
    setSubmitted(true);

    try {
      if (person.name.trim()) {
        let _persons = [...persons];
        let _person = { ...person };

        if (person.id) {
          const index = findIndexById(person.id);

          let formData = new FormData();
          formData.append("name", _person.name);
          formData.append("description", _person.description);
          formData.append("position", _person.position);
          formData.append("SortOrder", parseInt(_person.sortOrder));
          if (file != null) {
            formData.append("image", file);
          }
          if (signatureFile != null) {
            formData.append("Signature", signatureFile);
          }

          const response = await axios.put(
            process.env.REACT_APP_BACKEND_URL + "api/People/" + person.id,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          setFile(null);

          if (response.data.success) {
            _persons[index] = _person;
            if (response.data.fileName !== "")
              _persons[index].imageUrl = response.data.fileName;
            if (response.data.signature !== "")
              _persons[index].signature = response.data.signature;

            toast.current.show({
              severity: "success",
              summary: "Successful",
              detail: "Person Updated",
              life: 3000,
            });
          } else {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Person Not Updated",
              life: 3000,
            });
          }
        } else {
          _person.id = createId();

          let formData = new FormData();
          formData.append("name", _person.name);
          formData.append("description", _person.description);
          formData.append("position", _person.position);
          formData.append("SortOrder", parseInt(_person.sortOrder));
          if (file != null) {
            formData.append("image", file);
          }
          if (signatureFile != null) {
            formData.append("Signature", signatureFile);
          }

          const response = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "api/People",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          setLoading(false);
          setFile(null);

          if (response.data.success) {
            _persons.push(response.data.person);

            toast.current.show({
              severity: "success",
              summary: "Successful",
              detail: "Person Created",
              life: 3000,
            });
          } else {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Person Not Created",
              life: 3000,
            });
          }
        }

        setpersons(_persons);
        setpersonDialog(false);
        setperson(emptyperson);
      }
    } catch (error) {
      console.error("Error during save operation:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          "An error occurred while saving the person. Please try again later.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const editProduct = (person) => {
    setperson({ ...person });
    setpersonDialog(true);
  };

  const confirmDeleteProduct = (person) => {
    setperson(person);
    setDeletepersonDialog(true);
  };

  const deleteProduct = async () => {
    setLoading(true);
    const response = await axios.delete(
      process.env.REACT_APP_BACKEND_URL + "api/People/" + person.id
    );
    setLoading(false);
    if (response.data.success) {
      let _persons = persons.filter((val) => val.id !== person.id);
      setpersons(_persons);
      setDeletepersonDialog(false);
      setperson(emptyperson);
      toast.current.show({
        severity: "success",
        summary: "Successful",
        detail: "Person Deleted",
        life: 3000,
      });
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Person Not Deleted",
        life: 3000,
      });
    }
  };

  const findIndexById = (id) => {
    let index = -1;

    for (let i = 0; i < persons.length; i++) {
      if (persons[i].id === id) {
        index = i;
        break;
      }
    }

    return index;
  };

  const createId = () => {
    let id = "";
    let chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for (let i = 0; i < 5; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return id;
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const confirmDeleteSelected = () => {
    setDeletepersonsDialog(true);
  };

  const deleteSelectedProducts = async () => {
    setLoading(true);
    const ids = selectedpersons.map((person) => person.id);
    const idsString = ids.join(",");
    let data = { Ids: idsString };
    const response = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "api/People/DeletePersonByIds",
      data
    );
    setLoading(false);
    if (response.data.success) {
      let _persons = persons.filter((val) => !selectedpersons.includes(val));
      setpersons(_persons);
      setDeletepersonsDialog(false);
      setSelectedpersons(null);
      toast.current.show({
        severity: "success",
        summary: "Successful",
        detail: "Persons Deleted",
        life: 3000,
      });
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Persons Not Deleted",
        life: 3000,
      });
    }
  };
  const onFileUpload = async (event) => {
    const file = event.files[0];

    const resizedImage = await resizeFile(file); // Resize the image

    // Create a new File from the blob with the correct name and MIME type
    const newFile = new File([resizedImage], file.name, {
      type: resizedImage.type || "image/jpeg", // Set MIME type
    });
    setFile(newFile);
    //setFile(file);

    setperson((prevperson) => ({
      ...prevperson,
      imageUrl: process.env.REACT_APP_BACKEND_URL_IMAGES + newFile.name, // Save the file name as the image path for simplicity
    }));
  };
  const onSignatureFileUpload = async (event) => {
    const file = event.files[0];

    const resizedImage = await resizeFile(file); // Resize the image

    // Create a new File from the blob with the correct name and MIME type
    const newFile = new File([resizedImage], file.name, {
      type: resizedImage.type || "image/jpeg", // Set MIME type
    });
    setSignatureFile(newFile);
    //setFile(file);

    setperson((prevperson) => ({
      ...prevperson,
      signature: process.env.REACT_APP_BACKEND_URL_IMAGES + newFile.name, // Save the file name as the image path for simplicity
    }));
  };
  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || "";
    let _person = { ...person };

    _person[`${name}`] = val;

    setperson(_person);
  };
  const leftToolbarTemplate = () => {
    return (
      <div className="d-flex align-items-center justify-content-center gap-5">
        <h4 className="m-0 title-of-table">Manage Persons</h4>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            type="search"
            style={{ border: "1px solid black" }}
            onInput={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search..."
          />
        </span>
      </div>
    );
  };
  const rightToolbarTemplate = () => {
    return (
      <div className="d-flex flex-wrap gap-2">
        <Button
          label="New"
          icon="pi pi-plus"
          severity="success"
          onClick={openNew}
        />
        <Button
          label="Export"
          icon="pi pi-upload"
          className="p-button-help"
          onClick={exportCSV}
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          onClick={confirmDeleteSelected}
          disabled={!selectedpersons || !selectedpersons.length}
        />
      </div>
    );
  };
  const imageBodyTemplate = (rowData) => {
    return (
      <img
        loading="lazy"
        src={process.env.REACT_APP_BACKEND_URL_IMAGES + rowData.imageUrl}
        alt={rowData.imageUrl}
        className="shadow-2 border-round"
        style={{ width: "64px" }}
      />
    );
  };
  const SignatureimageBodyTemplate = (rowData) => {
    return (
      <img
        loading="lazy"
        src={process.env.REACT_APP_BACKEND_URL_IMAGES + rowData.signature}
        alt={rowData.signature}
        className="shadow-2 border-round"
        style={{ width: "64px" }}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          style={{ marginRight: "10px" }}
          onClick={() => editProduct(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDeleteProduct(rowData)}
        />
      </React.Fragment>
    );
  };
  const header = (
    <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
      {/* <h4 className="m-0">Manage persons</h4> */}
      {/* <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </span> */}
    </div>
  );
  const productDialogFooter = (
    <React.Fragment>
      <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} />
      <Button
        label="Save"
        loading={loading}
        icon="pi pi-check"
        onClick={saveperson}
      />
    </React.Fragment>
  );
  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteProductDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        loading={loading}
        onClick={deleteProduct}
      />
    </React.Fragment>
  );
  const deleteProductsDialogFooter = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        outlined
        onClick={hideDeleteProductsDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        severity="danger"
        loading={loading}
        onClick={deleteSelectedProducts}
      />
    </React.Fragment>
  );
  return (
    <div className="col-12">
      <div className="persons-container">
        <Toast ref={toast} />
        <div className="card">
          <Toolbar
            className="mb-3"
            start={leftToolbarTemplate}
            end={rightToolbarTemplate}
          ></Toolbar>

          <DataTable
            ref={dt}
            value={persons}
            selection={selectedpersons}
            onSelectionChange={(e) => setSelectedpersons(e.value)}
            dataKey="id"
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} persons"
            globalFilter={globalFilter}
            header={header}
          >
            <Column selectionMode="multiple" exportable={false}></Column>
            <Column
              field="id"
              header="Code"
              sortable
              width="150px"
              style={{ textAlign: "center" }}
            ></Column>
            <Column
              field="imageUrl"
              header="Image"
              body={imageBodyTemplate}
            ></Column>
            <Column
              field="name"
              header="Name"
              sortable
              style={{ minWidth: "12rem" }}
            ></Column>
            <Column
              field="position"
              header="Position"
              sortable
              style={{ minWidth: "12rem" }}
              body={(rowData) => rowData.position || ""}
            />
            <Column
              field="signature"
              header="Signature"
              body={SignatureimageBodyTemplate}
            ></Column>
            <Column
              field="sortOrder"
              sortable
              header="Order"
              style={{ textAlign: "center" }}
            ></Column>
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ minWidth: "12rem" }}
            ></Column>
          </DataTable>
        </div>

        <Dialog
          visible={personDialog}
          style={{ width: "32rem" }}
          breakpoints={{ "960px": "75vw", "641px": "90vw" }}
          header="person Details"
          modal
          className="p-fluid"
          footer={productDialogFooter}
          onHide={hideDialog}
        >
          {person.imageUrl && (
            <div className="image-container">
              <img
                loading="lazy"
                src={process.env.REACT_APP_BACKEND_URL_IMAGES + person.imageUrl}
                alt={person.imageUrl}
                className="product-image block m-auto pb-3"
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="name" className="font-bold mb-1">
              Name :
            </label>
            <InputText
              id="name"
              value={person.name}
              onChange={(e) => onInputChange(e, "name")}
              required
              className={classNames({
                "p-invalid": submitted && !person.name,
              })}
            />
            {submitted && !person.name && (
              <small className="p-error">Name is required.</small>
            )}
          </div>
          <div className="field">
            <label htmlFor="position" className="font-bold mb-1 mt-3">
              Position :
            </label>
            <InputText
              id="position"
              value={person.position}
              onChange={(e) => onInputChange(e, "position")}
            />
          </div>
          <div className="field">
            <label htmlFor="description" className="font-bold mt-3 mb-1">
              Description :
            </label>
            <Editor
              id="description"
              value={person.description}
              style={{ height: "100px" }}
              onTextChange={(e) =>
                onInputChange({ target: { value: e.htmlValue } }, "description")
              }
              required
              className={classNames({
                "p-invalid": submitted && !person.description,
              })}
            />
          </div>

          <div className="field">
            <label htmlFor="sortOrder" className="font-bold mt-3 mb-1">
              Order :
            </label>
            <InputText
              id="sortOrder"
              type="number"
              value={person.sortOrder}
              onChange={(e) => onInputChange(e, "sortOrder")}
              required
              className={classNames({
                "p-invalid": submitted && !person.sortOrder,
              })}
            />
            {submitted && !person.sortOrder && (
              <small className="p-error">sortOrder is required.</small>
            )}
          </div>
          <div className="field">
            <label htmlFor="image" className="mt-3 font-bold mb-1">
              Image :
            </label>
            <FileUpload
              name="image"
              mode="basic"
              accept="image/*"
              onSelect={onFileUpload}
            />
          </div>
          <div className="field">
            <label htmlFor="signature" className="mt-3 font-bold mb-1">
              Signature :
            </label>
            <FileUpload
              name="signature"
              mode="basic"
              accept="image/*"
              onSelect={onSignatureFileUpload}
            />
          </div>
        </Dialog>

        <Dialog
          visible={deletepersonDialog}
          style={{ width: "32rem" }}
          breakpoints={{ "960px": "75vw", "641px": "90vw" }}
          header="Confirm"
          modal
          footer={deleteProductDialogFooter}
          onHide={hideDeleteProductDialog}
        >
          <div className="confirmation-content">
            <i
              className="pi pi-exclamation-triangle mr-3"
              style={{
                textAlign: "center",
                fontSize: "2rem",
                width: "100%",
                color: "#6659ff",
              }}
            />
            <div>
              <p style={{ textAlign: "center", marginBottom: "5px" }}>
                Are you sure you want to delete:
              </p>
              {person && (
                <p style={{ textAlign: "center", marginBottom: "0" }}>
                  <b>{person.name}</b>
                </p>
              )}
            </div>
          </div>
        </Dialog>

        <Dialog
          visible={deletepersonsDialog}
          style={{ width: "32rem" }}
          breakpoints={{ "960px": "75vw", "641px": "90vw" }}
          header="Confirm"
          modal
          footer={deleteProductsDialogFooter}
          onHide={hideDeleteProductsDialog}
        >
          <div className="confirmation-content">
            <i
              className="pi pi-exclamation-triangle mr-3"
              style={{
                textAlign: "center",
                fontSize: "2rem",
                width: "100%",
                color: "#6659ff",
              }}
            />
            {person && (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  marginBottom: "25px",
                }}
              >
                Are you sure you want to delete the selected persons?
              </p>
            )}
          </div>
        </Dialog>
      </div>
    </div>
  );
}

export default Person;
